import * as React from 'react';
import styled, { css } from 'styled-components';
import InputGroup from '../inputGroup/InputGroup';
import classNames from 'classnames';

// ############################ End Styles ###########################
// tslint:disable:variable-name

const SInputGroupWrapInner = styled.div<{ error: boolean; disabled: boolean }>(({ theme, disabled }) => {
  return css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    border-radius: ${theme.borderRadiusCommon};

    &:focus-within {
      border-color: ${theme.colors.light};
    }

    ${disabled && `opacity: 0.5`};

    .input-wrapper + .input-wrapper {
      margin-left: 13px;

      &:before {
        content: '_';
        margin-left: -15px;
        padding: 2px;
        display: flex;
        align-self: center;
        color: ${theme.colors.placeholder2};
        font-size: ${theme.font.size.smallMedium};
        font-weight: ${theme.font.weight.medium};
      }
    }
  `;
});

const SInputWrap = styled.div(({ theme }) => ({
  height: '100%',
  maxWidth: '73px',
  borderRadius: theme.borderRadiusCommon,
  margin: '0 auto',
  backgroundColor: theme.colors.grayWhite,
  display: 'flex',
}));

const SInput = styled.input(({ theme }) => ({
  fontSize: theme.font.size.smallMedium,
  boxSizing: 'border-box',
  color: theme.colors.primaryBackground,
  backgroundColor: 'transparent',
  borderWidth: 0,
  appearance: 'none',
  outline: 'none',
  width: '100%',
  padding: '8px',
  textAlign: 'center',
  '&:invalid': {
    boxShadow: 'none',
  },
  '&::placeholder': {
    fontWeight: theme.font.weight.medium,
    color: theme.colors.placeholder2,
    fontSize: theme.font.size.smallMedium,
  },
}));

// tslint:enable:variable-name
// ############################ End Styles ###########################

type SSNSplittedInputState = {
  number1: string;
  number2: string;
  number3: string;
};

enum SSNSplittedInputNumbers {
  number1 = 'number1',
  number2 = 'number2',
  number3 = 'number3',
}

export interface ISSNSplittedInputProps {
  onFilled?: (event: any) => void;
  onChangeCallback?: (event: React.ChangeEvent<HTMLInputElement>, args: { value?: string }) => void;
  value: string;
  className?: string;
  inputClassName?: string;
}

interface ISSNSplittedInputPartProps {
  name: string;
  type?: string;
  className?: string;
  dataAt: string;
  value: string;
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyUp: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const getInputLength = (inputNumber: string) => {
  switch (inputNumber) {
    case SSNSplittedInputNumbers.number1:
      return 3;
    case SSNSplittedInputNumbers.number2:
      return 2;
    case SSNSplittedInputNumbers.number3:
      return 4;
    default:
      return 0;
  }
};

// tslint:disable-next-line:variable-name
const CreditCardNumberPart: React.FC<ISSNSplittedInputPartProps> = (props) => {
  const { dataAt, value, className, ...inputProps } = props;
  const inputLength = getInputLength(inputProps.name);

  return (
    <SInputWrap className={classNames('input-wrapper', className)}>
      <SInput
        placeholder={'#'.repeat(inputLength)}
        maxLength={inputLength}
        type={inputProps.type || 'text'}
        inputMode="numeric"
        pattern="[0-9]*"
        {...inputProps}
        data-at={dataAt}
      />
    </SInputWrap>
  );
};

export class SSNSplittedInput extends React.Component<ISSNSplittedInputProps, SSNSplittedInputState> {
  public static defaultProps = {
    className: '',
  };

  public state = {
    number1: '',
    number2: '',
    number3: '',
  };

  public handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // Removed for now - people don't like it - auto delete current input on focus
    // this.setState({ ...this.state, [event.currentTarget.name]: '' });
    // if (this.props.onFilled) {
    //   this.props.onFilled({ target: { value: '', name: 'ssn' }});
    // }
  };

  public handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const el = event.currentTarget;
    const aNumber = /^\d+$/.test(el.value);
    if (!aNumber) {
      const newValue = el.value.length <= 1 ? '' : el.value.slice(0, -1);
      this.setState({ ...this.state, [el.name]: newValue });
    }
    const val = this.state.number1 + this.state.number2 + this.state.number3;
    if (val.length === 9 && /^\d+$/.test(val)) {
      if (this.props.onFilled) {
        this.props.onFilled({ target: { value: val, name: 'ssn', el } });
      }
    } else if (
      el.value.length === getInputLength(el.name) &&
      aNumber &&
      el.parentElement &&
      el.parentElement.nextElementSibling &&
      el.parentElement.nextElementSibling.firstChild &&
      (el.parentElement.nextElementSibling.firstChild as HTMLInputElement).value.length < getInputLength(el.name)
    ) {
      (el.parentElement.nextElementSibling.firstChild as HTMLElement).focus();
    }
  };

  public handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onChangeCallback } = this.props;
    const valueCandidate = event.currentTarget.value;
    this.setState({ ...this.state, [event.currentTarget.name]: valueCandidate }, () => {
      const { number1, number2, number3 } = this.state;
      onChangeCallback?.(event, { value: [number1, number2, number3].join('') });
    });
  };

  public static getDerivedStateFromProps(nextProps: ISSNSplittedInputProps, prevState: SSNSplittedInputState) {
    if (
      prevState.number1 === '' &&
      prevState.number2 === '' &&
      prevState.number3 === '' &&
      nextProps.value &&
      nextProps.value.length === 9
    ) {
      return {
        number1: nextProps.value.substring(0, 3),
        number2: nextProps.value.substring(3, 5),
        number3: nextProps.value.substring(5, 9),
      };
    }
    return {};
  }

  public render() {
    const commonInputProps = {
      onFocus: this.handleFocus,
      onKeyUp: this.handleKeyUp,
      onChange: this.handleChange,
      className: this.props.inputClassName,
    };

    return (
      <InputGroup className={this.props.className} dataAt="ssnInput" dataTestId="ssn-input-group">
        <SInputGroupWrapInner error={false} disabled={false}>
          <CreditCardNumberPart
            {...commonInputProps}
            name="number1"
            value={this.state.number1}
            dataAt="ssnInputNum1"
            data-testid="ssn-input-1"
          />
          <CreditCardNumberPart
            {...commonInputProps}
            name="number2"
            value={this.state.number2}
            dataAt="ssnInputNum2"
            data-testid="ssn-input-2"
          />
          <CreditCardNumberPart
            {...commonInputProps}
            name="number3"
            value={this.state.number3}
            dataAt="ssnInputNum3"
            type="password"
            data-testid="ssn-input-3"
          />
        </SInputGroupWrapInner>
      </InputGroup>
    );
  }
}
